import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import type { CustodyHandoverWithUsers } from '@kinapp/shared';

interface HandoverLogProps {
  familyId: string;
  dependentId: string;
  currentUserId?: string;
  onUpdate?: () => void;
}

export function HandoverLog({ familyId, dependentId, currentUserId, onUpdate }: HandoverLogProps) {
  const [handovers, setHandovers] = useState<CustodyHandoverWithUsers[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadHandovers();
  }, [familyId, dependentId]);

  const loadHandovers = async () => {
    try {
      setLoading(true);
      const data = await api.getCustodyHandovers(familyId, dependentId);
      setHandovers(data);
      setError('');
    } catch (err: any) {
      if (err.status === 404) {
        setHandovers([]);
      } else {
        setError(err.message || 'Failed to load handovers');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (handoverId: string) => {
    try {
      await api.confirmHandover(familyId, handoverId, {
        actualTime: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
      });
      await loadHandovers();
      onUpdate?.();
    } catch (err: any) {
      alert('Failed to confirm handover: ' + err.message);
    }
  };

  if (loading) {
    return <div className="text-gray-500 text-sm">Loading handovers...</div>;
  }

  if (error) {
    return <div className="text-red-600 text-sm">{error}</div>;
  }

  if (handovers.length === 0) {
    return <div className="text-gray-500 text-sm">No handovers logged yet.</div>;
  }

  return (
    <div className="space-y-3">
      {handovers.map((handover) => {
        const isPending = handover.status === 'PENDING';
        const isReceiver = currentUserId === handover.toParentId;

        return (
          <div
            key={handover.id}
            className={`p-3 rounded-lg border ${
              isPending ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm font-medium text-gray-900">
                  {new Date(handover.date).toLocaleDateString('en-GB', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                  })}
                  {handover.scheduledTime && (
                    <span className="text-gray-500"> at {handover.scheduledTime}</span>
                  )}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  {handover.fromParent.name} &rarr; {handover.toParent.name}
                </div>
                {handover.notes && (
                  <div className="text-xs text-gray-500 mt-1">{handover.notes}</div>
                )}
                {handover.status === 'CONFIRMED' && handover.actualTime && (
                  <div className="text-xs text-green-600 mt-1">
                    Confirmed at {handover.actualTime}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <span
                  className={`text-xs px-2 py-0.5 rounded ${
                    handover.status === 'CONFIRMED'
                      ? 'bg-green-100 text-green-700'
                      : handover.status === 'MISSED'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-amber-100 text-amber-700'
                  }`}
                >
                  {handover.status}
                </span>

                {isPending && isReceiver && (
                  <button
                    onClick={() => handleConfirm(handover.id)}
                    className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Confirm
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
